import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { STATUS } from "@/constants";
import {
  mapChatSessionSummary,
  type ChatSessionListResponse,
  type ChatSessionSummary,
  type UpdateChatTitleResponse,
} from "@/features/chat/model/chatHistoryTypes";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

export type ChatHistoryState = {
  sessions: ChatSessionSummary[];
  listStatus: "idle" | "loading" | "succeeded" | "failed";
  updateStatus: "idle" | "loading" | "succeeded" | "failed";
  deleteStatus: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
};

export const chatHistoryInitialState: ChatHistoryState = {
  sessions: [],
  listStatus: STATUS.IDLE,
  updateStatus: STATUS.IDLE,
  deleteStatus: STATUS.IDLE,
  error: null,
};

function isChatSessionListResponse(value: unknown): value is ChatSessionListResponse {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return Array.isArray(row.sessions);
}

function isUpdateChatTitleResponse(value: unknown): value is UpdateChatTitleResponse {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return typeof row.session_id === "string" && typeof row.created_at === "string";
}

export const loadChatSessions = createAsyncThunk<
  ChatSessionSummary[],
  void,
  { rejectValue: string }
>("chatHistory/loadChatSessions", async (_, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/chat/sessions", { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }

    const data: unknown = await res.json();
    if (!isChatSessionListResponse(data)) {
      return rejectWithValue("Unexpected response from chat service.");
    }

    return data.sessions.map(mapChatSessionSummary);
  } catch {
    return rejectWithValue("Unable to reach the chat service.");
  }
});

type UpdateChatTitleArgs = {
  sessionId: string;
  title: string;
};

export const updateChatTitle = createAsyncThunk<
  ChatSessionSummary,
  UpdateChatTitleArgs,
  { rejectValue: string }
>("chatHistory/updateChatTitle", async ({ sessionId, title }, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/chat/sessions/${sessionId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title }),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }

    const data: unknown = await res.json();
    if (!isUpdateChatTitleResponse(data)) {
      return rejectWithValue("Unexpected response from chat service.");
    }

    return mapChatSessionSummary(data);
  } catch {
    return rejectWithValue("Unable to reach the chat service.");
  }
});

export const deleteChatSession = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>("chatHistory/deleteChatSession", async (sessionId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/chat/sessions/${sessionId}`, { method: "DELETE" });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }

    return sessionId;
  } catch {
    return rejectWithValue("Unable to reach the chat service.");
  }
});

const chatHistorySlice = createSlice({
  name: "chatHistory",
  initialState: chatHistoryInitialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadChatSessions.pending, (state) => {
        state.listStatus = STATUS.LOADING;
        state.error = null;
      })
      .addCase(loadChatSessions.fulfilled, (state, action) => {
        state.listStatus = STATUS.SUCCEEDED;
        state.sessions = action.payload;
      })
      .addCase(loadChatSessions.rejected, (state, action) => {
        state.listStatus = STATUS.FAILED;
        state.error = action.payload ?? "Failed to load chats.";
      })
      .addCase(updateChatTitle.pending, (state) => {
        state.updateStatus = STATUS.LOADING;
        state.error = null;
      })
      .addCase(updateChatTitle.fulfilled, (state, action) => {
        state.updateStatus = STATUS.SUCCEEDED;
        const index = state.sessions.findIndex(
          (session) => session.sessionId === action.payload.sessionId,
        );
        if (index >= 0) {
          state.sessions[index] = {
            ...action.payload,
            userName: state.sessions[index].userName,
            domainName: state.sessions[index].domainName,
            hasTerminatedTurn: state.sessions[index].hasTerminatedTurn,
          };
        }
      })
      .addCase(updateChatTitle.rejected, (state, action) => {
        state.updateStatus = STATUS.FAILED;
        state.error = action.payload ?? "Failed to update chat title.";
      })
      .addCase(deleteChatSession.pending, (state) => {
        state.deleteStatus = STATUS.LOADING;
        state.error = null;
      })
      .addCase(deleteChatSession.fulfilled, (state, action) => {
        state.deleteStatus = STATUS.SUCCEEDED;
        state.sessions = state.sessions.filter(
          (session) => session.sessionId !== action.payload,
        );
      })
      .addCase(deleteChatSession.rejected, (state, action) => {
        state.deleteStatus = STATUS.FAILED;
        state.error = action.payload ?? "Failed to delete chat.";
      });
  },
});

export const chatHistoryReducer = chatHistorySlice.reducer;
